import React, { useMemo } from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

// --- Helper Functions (Reused) ---
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isNumericHeader = (header) => {
    if (header.toUpperCase().includes('ACCOUNT') || header.toUpperCase().includes('CUSTOMER')) {
        return false;
    }
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

const FOOTER_TOTAL_KEYS = [
    'CASH SALES', 'NETCASH SALES', 'NETCASH SALESRETURN', 'CREDIT SALES', 
    'NETCREDIT SALES', 'NETSALES RETURN', 'VAT AMT', 'COMMISSION', 
    'NET SALES', 'NET SALES EXCLVAT'
];
const DESCRIPTIVE_KEY = 'ACCOUNT';

const getColumnWidths = (dataColumnsCount) => {
    const descriptiveWeight = 2;
    const standardWeight = 1;
    const totalWeight = descriptiveWeight + (dataColumnsCount * standardWeight);
    const descriptiveWidth = (descriptiveWeight / totalWeight) * 100;
    const standardWidth = (standardWeight / totalWeight) * 100;

    return {
        descriptiveWidth: `${descriptiveWidth.toFixed(2)}%`,
        standardWidth: `${standardWidth.toFixed(2)}%`,
    };
};

// --- Styling Definitions (FIXED tableCell) ---
const BORDER_COLOR = '#999'; 
const BORDER_WIDTH = 1;
const HEADER_COLOR = '#e0f7fa';
const FOOTER_COLOR = '#80cbc4';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: 20,
  },
  title: {
    fontSize: 14,
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  table: { 
    display: 'table', 
    width: '100%', 
    borderStyle: 'solid',
    borderWidth: BORDER_WIDTH,
    borderColor: BORDER_COLOR,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: { 
    flexDirection: 'row', 
    width: '100%', 
    alignItems: 'stretch',
  },
  tableHeader: { 
    backgroundColor: HEADER_COLOR,
    fontWeight: 'bold',
  },
  // Base Cell Styles
  tableCell: { 
    borderColor: BORDER_COLOR,
    borderStyle: 'solid', 
    borderBottomWidth: BORDER_WIDTH,
    borderRightWidth: BORDER_WIDTH,
    borderLeftWidth: 0, 
    borderTopWidth: 0,
    
    padding: 4, 
    fontSize: 7, 
    // 💡 FIX: Re-introduce a minimal flexGrow to enable proper column stretching
    flexGrow: 1, 
    minWidth: 50 // Optional: Retain a minimal min-width for safety
  },
  descriptiveCell: {
    textAlign: 'left',
    borderLeftWidth: BORDER_WIDTH, 
  },
  numericCell: {
    textAlign: 'right',
  },
  tableFooterRow: { 
    flexDirection: 'row', 
    width: '100%',
    backgroundColor: FOOTER_COLOR,
    borderTopWidth: 3, 
    borderTopColor: '#000', 
  },
  footerCell: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'right',
    padding: 6,
  }
});


// --- PDF Component Content (Rendering logic remains the same) ---
const SalesReportContent = ({ rows }) => { 

    const descriptiveColumnKey = DESCRIPTIVE_KEY;
    const allKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
    const displayKeys = allKeys.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', descriptiveColumnKey].includes(key)
    );
    
    const { descriptiveWidth: descriptiveColWidth, standardWidth: standardColWidth } = getColumnWidths(displayKeys.length);

    const internalGrandTotals = useMemo(() => {
        if (rows.length === 0) return {};
        const keysToTotal = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        const totals = {};
        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }
        return totals;
    }, [rows, displayKeys]);
    
    const totalValueKeys = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
    const descriptiveHeader = descriptiveColumnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

    let descriptiveSpanIndex = 0;
    if (totalValueKeys.length > 0) {
        const firstTotalKey = totalValueKeys[0];
        descriptiveSpanIndex = displayKeys.indexOf(firstTotalKey);
        if (descriptiveSpanIndex === -1) descriptiveSpanIndex = displayKeys.length;
    }
    
    const totalDescriptiveWidthValue = parseFloat(descriptiveColWidth) + (descriptiveSpanIndex * parseFloat(standardColWidth));
    const totalDescriptiveWidth = `${totalDescriptiveWidthValue.toFixed(2)}%`;

    if (rows.length === 0) return <Text style={{ textAlign: 'center' }}>No sales data found for the selected filters.</Text>;

    return (
        <View style={styles.table}>
            {/* Header Row */}
            <View style={[styles.tableRow, styles.tableHeader]}>
                {/* Descriptive Header */}
                <Text style={[styles.tableCell, styles.descriptiveCell, { width: descriptiveColWidth }]}>
                    {descriptiveHeader}
                </Text>
                {/* Dynamic Headers */}
                {displayKeys.map(key => (
                    <Text
                        key={key}
                        style={[
                            styles.tableCell, 
                            isNumericHeader(key) ? styles.numericCell : styles.descriptiveCell,
                            { width: standardColWidth }
                        ]}
                    >
                        {key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                    </Text>
                ))}
            </View>

            {/* Body Rows */}
            {rows.map((row, index) => (
                <View key={`agg-acc-row-${index}`} style={styles.tableRow}>
                    {/* Descriptive Cell */}
                    <Text style={[styles.tableCell, styles.descriptiveCell, { backgroundColor: '#ffffff', width: descriptiveColWidth }]}>
                        {row[descriptiveColumnKey]}
                    </Text>
                    {/* Data Cells */}
                    {displayKeys.map(key => (
                        <Text 
                            key={key} 
                            style={[
                                styles.tableCell, 
                                isNumericHeader(key) ? styles.numericCell : styles.descriptiveCell,
                                { width: standardColWidth }
                            ]}
                        >
                            {isNumericHeader(key) ? formatNumber(row[key]) : row[key]}
                        </Text>
                    ))}
                </View>
            ))}

            {/* Footer - Grand Totals */}
            {Object.keys(internalGrandTotals).length > 0 && (
                <View style={styles.tableFooterRow}>
                    {/* GRAND TOTAL label cell */}
                    <Text style={[
                        styles.footerCell, 
                        { 
                            width: totalDescriptiveWidth,
                            textAlign: 'right', 
                            paddingRight: 10,
                            borderLeftWidth: BORDER_WIDTH,
                            borderRightWidth: BORDER_WIDTH, 
                            borderColor: BORDER_COLOR,
                            borderStyle: 'solid',
                        }
                    ]}>
                        GRAND TOTAL:
                    </Text>
                    
                    {/* Total cells */}
                    {displayKeys.slice(descriptiveSpanIndex).map((key) => {
                        const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                        
                        return (
                            <Text 
                                key={key} 
                                style={[
                                    styles.footerCell, 
                                    styles.numericCell, 
                                    { 
                                        width: standardColWidth,
                                        borderRightWidth: BORDER_WIDTH,
                                        borderColor: BORDER_COLOR,
                                        borderStyle: 'solid',
                                    } 
                                ]}
                            >
                                {isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}
                            </Text>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

// Wrapper Document Component
const SalesTableAccountAggregatedPDF = ({ rows, reportTitle = "Sales Account Aggregated Report" }) => (
  <Document>
    <Page size="A4" style={styles.page} orientation="landscape">
        <Text style={styles.title}>{reportTitle}</Text>
        
        <SalesReportContent rows={rows} />
    </Page>
  </Document>
);

export default SalesTableAccountAggregatedPDF;